import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import api from '../services/api';
import { toast } from 'react-toastify';
import { useAuth } from '../context/AuthContext';

const TIMEZONES = [
  'America/Guayaquil',
  'America/Bogota',
  'America/Lima',
  'America/Santiago',
  'America/Buenos_Aires',
  'America/Caracas',
  'America/La_Paz',
  'America/Asuncion',
  'America/Montevideo',
  'America/Mexico_City',
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'Europe/Madrid',
  'UTC',
];

const inputStyle = {
  border: '2px solid rgba(59,130,246,0.3)',
  borderRadius: '8px',
  padding: '0.75rem',
  fontSize: '1rem',
  backgroundColor: '#fafafa',
  width: '100%',
  boxSizing: 'border-box',
};

const readonlyStyle = {
  ...inputStyle,
  backgroundColor: '#f3f4f6',
  color: '#6b7280',
  cursor: 'not-allowed',
};

const DatosNegocio = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [guardando, setGuardando] = useState(false);
  const [tiposNegocio, setTiposNegocio] = useState([]);
  const [form, setForm] = useState({
    nombre_negocio: '',
    tipo_negocio: '',
    telefono: '',
    whatsapp_number: '',
    timezone: 'America/Guayaquil',
  });
  const [info, setInfo] = useState({
    email: '',
    codigo_tenant: '',
    plan_nombre: '',
    estado_suscripcion: '',
    fecha_fin_suscripcion: '',
  });

  useEffect(() => {
    const cargar = async () => {
      try {
        const [{ data: negocio }, { data: tipos }] = await Promise.all([
          api.get('/tenant/datos'),
          api.get('/tipos-negocio'),
        ]);
        setForm({
          nombre_negocio: negocio.nombre_negocio || '',
          tipo_negocio: negocio.tipo_negocio || '',
          telefono: negocio.telefono || '',
          whatsapp_number: negocio.whatsapp_number || '',
          timezone: negocio.timezone || 'America/Guayaquil',
        });
        setInfo({
          email: negocio.email || '',
          codigo_tenant: negocio.codigo_tenant || '',
          plan_nombre: negocio.plan_nombre || '',
          estado_suscripcion: negocio.estado_suscripcion || '',
          fecha_fin_suscripcion: negocio.fecha_fin_suscripcion || '',
        });
        setTiposNegocio(tipos);
      } catch {
        toast.error('Error al cargar datos del negocio');
      } finally {
        setLoading(false);
      }
    };
    cargar();
  }, []);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.nombre_negocio.trim()) {
      toast.error('El nombre del negocio es obligatorio');
      return;
    }
    setGuardando(true);
    try {
      await api.put('/tenant/datos', form);
      toast.success('Datos del negocio actualizados');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Error al guardar');
    } finally {
      setGuardando(false);
    }
  };

  if (user?.rol !== 'admin' && user?.rol !== 'superadmin') {
    return (
      <Layout>
        <div style={{ textAlign: 'center', marginTop: '4rem', color: '#6b7280' }}>
          No tienes permiso para acceder a esta sección.
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div style={{ maxWidth: '700px', margin: '0 auto', padding: '1.5rem' }}>
        <div style={{ marginBottom: '1.5rem' }}>
          <h1 style={{ fontSize: '1.5rem', fontWeight: '700', margin: 0 }}>Mi Negocio</h1>
          <p style={{ color: 'var(--text-secondary)', marginTop: '0.25rem', fontSize: '0.95rem' }}>
            Actualiza los datos de tu empresa
          </p>
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--text-secondary)' }}>
            Cargando...
          </div>
        ) : (
          <>
            {/* Información de solo lectura */}
            <div style={{
              background: 'var(--surface)',
              border: '1px solid var(--border)',
              borderRadius: '12px',
              padding: '1.25rem',
              marginBottom: '1.5rem',
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: '1rem',
            }}>
              <div>
                <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: '0.25rem', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  Código de empresa
                </div>
                <div style={{ fontFamily: 'monospace', fontSize: '1.1rem', fontWeight: '700', color: 'var(--primary)' }}>
                  {info.codigo_tenant}
                </div>
              </div>
              <div>
                <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: '0.25rem', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  Email de acceso
                </div>
                <div style={{ fontSize: '0.95rem' }}>{info.email}</div>
              </div>
              <div>
                <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: '0.25rem', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  Plan activo
                </div>
                <div style={{ fontSize: '0.95rem' }}>{info.plan_nombre}</div>
              </div>
              <div>
                <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: '0.25rem', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  Suscripción hasta
                </div>
                <div style={{ fontSize: '0.95rem' }}>
                  {info.fecha_fin_suscripcion
                    ? new Date(info.fecha_fin_suscripcion).toLocaleDateString('es-EC', { day: '2-digit', month: 'long', year: 'numeric' })
                    : '—'}
                </div>
              </div>
            </div>

            {/* Formulario editable */}
            <form onSubmit={handleSubmit} style={{
              background: 'var(--surface)',
              border: '1px solid var(--border)',
              borderRadius: '12px',
              padding: '1.5rem',
            }}>
              <div style={{ display: 'grid', gap: '1.25rem' }}>

                <div>
                  <label style={{ display: 'block', fontWeight: '600', marginBottom: '0.5rem' }}>
                    Nombre del negocio <span style={{ color: '#dc2626' }}>*</span>
                  </label>
                  <input
                    name="nombre_negocio"
                    value={form.nombre_negocio}
                    onChange={handleChange}
                    style={inputStyle}
                    placeholder="Ej. Salón de belleza María"
                    maxLength={200}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', fontWeight: '600', marginBottom: '0.5rem' }}>
                    Tipo de negocio
                  </label>
                  <select
                    name="tipo_negocio"
                    value={form.tipo_negocio}
                    onChange={handleChange}
                    style={inputStyle}
                  >
                    <option value="">— Seleccionar —</option>
                    {tiposNegocio.map(t => (
                      <option key={t.id} value={t.nombre}>{t.icono} {t.nombre}</option>
                    ))}
                  </select>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                  <div>
                    <label style={{ display: 'block', fontWeight: '600', marginBottom: '0.5rem' }}>
                      Teléfono
                    </label>
                    <input
                      name="telefono"
                      value={form.telefono}
                      onChange={handleChange}
                      style={inputStyle}
                      placeholder="Ej. 0991234567"
                      maxLength={20}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontWeight: '600', marginBottom: '0.5rem' }}>
                      WhatsApp
                    </label>
                    <input
                      name="whatsapp_number"
                      value={form.whatsapp_number}
                      onChange={handleChange}
                      style={inputStyle}
                      placeholder="Ej. 0991234567"
                      maxLength={20}
                    />
                  </div>
                </div>

                <div>
                  <label style={{ display: 'block', fontWeight: '600', marginBottom: '0.5rem' }}>
                    Zona horaria
                  </label>
                  <select
                    name="timezone"
                    value={form.timezone}
                    onChange={handleChange}
                    style={inputStyle}
                  >
                    {TIMEZONES.map(tz => (
                      <option key={tz} value={tz}>{tz}</option>
                    ))}
                  </select>
                </div>

              </div>

              <div style={{ marginTop: '1.5rem', display: 'flex', justifyContent: 'flex-end' }}>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={guardando}
                  style={{ minWidth: '140px' }}
                >
                  {guardando ? 'Guardando...' : '💾 Guardar cambios'}
                </button>
              </div>
            </form>
          </>
        )}
      </div>
    </Layout>
  );
};

export default DatosNegocio;
